import { Button, FormInput, MoneyAlert } from '@/components';
import ErrorAlert from '@/components/molecules/ErrorAlert/ErrorAlert';
import { useReferral } from '@/components/providers/postauth/ReferralProvider';
import { useTradeableTokens } from '@/components/providers/postauth/TradeableTokensProvider';
import { useWalletsContext } from '@/components/providers/postauth/UserWalletsProvider';
import APP_CONFIG from '@/config/app';
import ReferralService from '@/services/ReferralService';
import { logDebug, logError, logInfo } from '@/utils/logger';
import { REFERRAL } from '@/utils/strings';
import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
const visualizeElementsAndSpacing = false;
const borderClassToVisualizeElementsAndSpacing = visualizeElementsAndSpacing
  ? 'border-white border'
  : '';
const { MAIN_GAP, MAIN_PADDING, WHOLESTEP_DECREMENT } = APP_CONFIG.STYLES;
const spacingConfig = {
  formGap: `gap${MAIN_GAP}`,
  formPadding: `p${MAIN_PADDING}`,
  inputGap: `gap${MAIN_GAP}${WHOLESTEP_DECREMENT}`,
};
interface ReferralFormData {
  friendReferralCode: string;
}
const COMPONENT_NAME = 'ApplyReferralCodeSection';
const ApplyReferralCodeSection: React.FC = () => {
  const [isApplying, setIsApplying] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [errorType, setErrorType] = useState<string | undefined>(undefined);
  const { refreshWalletsAndTransactions } = useWalletsContext();
  const { refreshTradeableTokens } = useTradeableTokens();
  const { hasUsedReferralCode, usedReferralCode, getApplyErrorMessage } =
    useReferral();
  useEffect(() => {
    logDebug('Component mounted', COMPONENT_NAME, {
      hasUsedReferralCode,
      usedReferralCode,
    });
  }, [hasUsedReferralCode, usedReferralCode]);
  const {
    register,
    handleSubmit,
    formState: { errors },
    clearErrors,
    setError: setFormError,
  } = useForm<ReferralFormData>();
  const onSubmit = useCallback(
    async (data: ReferralFormData) => {
      logInfo('Form submitted', `${COMPONENT_NAME}.onSubmit`, {
        referralCode: data.friendReferralCode,
      });
      setIsApplying(true);
      setLocalError(null);
      setErrorType(undefined);
      try {
        logDebug('Calling applyReferralCode', `${COMPONENT_NAME}.onSubmit`, {
          referralCode: data.friendReferralCode,
        });
        const serviceResult = await ReferralService.applyReferralCode(
          data.friendReferralCode,
        );
        logInfo(
          'ReferralService.applyReferralCode result',
          `${COMPONENT_NAME}.onSubmit`,
          {
            serviceResult,
            referralCode: data.friendReferralCode,
          },
        );
        if (serviceResult.success) {
          logInfo(
            'Referral code applied successfully',
            `${COMPONENT_NAME}.onSubmit`,
            { authorizationChanged: serviceResult.authorizationChanged },
          );
          if (serviceResult.authorizationChanged) {
            logInfo(
              'Authorization status changed after referral code application',
              `${COMPONENT_NAME}.onSubmit`,
            );
          }
          logInfo(
            'Data refresh handled by ReferralService',
            `${COMPONENT_NAME}.onSubmit`,
          );
          setShowThankYou(true);
          setIsApplying(false);
          return;
        } else {
          logInfo(
            'Failed to apply referral code',
            `${COMPONENT_NAME}.onSubmit`,
            { errorType: serviceResult.errorType },
          );
          setErrorType(serviceResult.errorType);
          const errorMessage = getApplyErrorMessage(serviceResult.errorType);
          setFormError('friendReferralCode', {
            type: 'manual',
            message: errorMessage.description,
          });
        }
      } catch (err) {
        logError(err, `${COMPONENT_NAME}.onSubmit`, {
          referralCode: data.friendReferralCode,
        });
        setErrorType('unknown');
        setLocalError(getApplyErrorMessage('unknown').description);
      } finally {
        setIsApplying(false);
      }
    },
    [
      setFormError,
      getApplyErrorMessage,
      refreshWalletsAndTransactions,
      refreshTradeableTokens,
    ],
  );
  let title = '';
  let description = '';
  let content = null;
  if (showThankYou) {
    title = REFERRAL.APPLY_SUCCESS_TITLE;
    description = REFERRAL.APPLY_SUCCESS_DESCRIPTION;
  } else if (hasUsedReferralCode) {
    title = REFERRAL.ALREADY_USED_TITLE;
    description = REFERRAL.ALREADY_USED_DESCRIPTION.replace(
      '[referral_code]',
      usedReferralCode || '',
    );
  } else {
    title = REFERRAL.WERE_YOU_REFERRED_TITLE;
    description = REFERRAL.WERE_YOU_REFERRED_DESCRIPTION;
    content = (
      <form
        onSubmit={(e) => {
          logDebug(
            'Form onSubmit event triggered',
            `${COMPONENT_NAME}.form.onSubmit`,
          );
          handleSubmit((data) => {
            logDebug(
              'handleSubmit callback executed',
              `${COMPONENT_NAME}.form.handleSubmit`,
              data,
            );
            onSubmit(data);
          })(e);
        }}
        className={`flex flex-col ${spacingConfig.inputGap} ${borderClassToVisualizeElementsAndSpacing}`}
      >
        <FormInput<ReferralFormData>
          name="friendReferralCode"
          label={REFERRAL.REFERRAL_CODE_LABEL}
          register={register}
          rules={{ required: REFERRAL.REFERRAL_CODE_REQUIRED }}
          errors={errors}
          clearErrors={clearErrors}
        />
        <Button
          type="submit"
          disabled={isApplying}
          fullWidth
          loading={isApplying}
        >
          {REFERRAL.APPLY_BUTTON_TEXT}
        </Button>
        {(errors.friendReferralCode || localError || errorType) && (
          <ErrorAlert
            title={
              errorType
                ? getApplyErrorMessage(errorType).title
                : REFERRAL.APPLY_ERROR_TITLE
            }
            description={
              errors.friendReferralCode?.message ||
              (errorType
                ? getApplyErrorMessage(errorType).description
                : localError || REFERRAL.APPLY_ERROR_DESCRIPTION)
            }
            className="mt-4"
          />
        )}
      </form>
    );
  }
  return (
    <MoneyAlert
      title={title}
      description={description}
      extraContent={content}
      className={borderClassToVisualizeElementsAndSpacing}
    />
  );
};
export default React.memo(ApplyReferralCodeSection);
